import {
  CreateBankInternalTransferErrorType,
  useCreateBankInternalTransfer,
  type CreateBankInternalTransferInput,
  type CreateBankInternalTransferOutput,
} from '@egw/api'
import { type GQLError } from '@egw/clients'
import { useTranslation } from '@egw/i18n'
import { Logger } from '@egw/logger'
import { type UseFormSetError } from 'react-hook-form'
import {
  TransferSuccessTypes,
  type BackendError,
  type EBackendError,
  type IGQLErrorResponse,
  type TransferFormInterface,
} from '../types'

const log = new Logger('useBankInvestmentTransfer Hook')

interface UseBankInvestmentTransferInterface {
  onSuccess: (_successType: TransferSuccessTypes) => void
  onError: (_errorType: CreateBankInternalTransferErrorType | EBackendError) => void
  onInlineError: UseFormSetError<TransferFormInterface>
}

export const useInternalBankTransfer = ({ onInlineError, onSuccess, onError }: UseBankInvestmentTransferInterface) => {
  const { t } = useTranslation('features-transfer')

  function onTransferSuccess(
    data: CreateBankInternalTransferOutput,
    variables?: CreateBankInternalTransferInput,
  ): void {
    const createBankInternalTransfer = data

    // Error Scenario
    if (createBankInternalTransfer.__typename === 'CreateBankInternalTransferError') {
      const errorType = createBankInternalTransfer.errorType

      switch (errorType) {
        case CreateBankInternalTransferErrorType.INSUFFICIENT_FUNDS:
          onInlineError('amount', { message: t(`transfer.internal_bank_transfer_error.${errorType}`) })
          break
        case CreateBankInternalTransferErrorType.FAILED_TO_CREATE_TRANSFER:
        case CreateBankInternalTransferErrorType.BAD_REQUEST:
          onError(errorType)
          break
        default:
          log.error('Error Type not being handled', { errorType })
          break
      }
    }

    // Success Scenario
    if (createBankInternalTransfer.__typename === 'CreateBankInternalTransfer') {
      onSuccess(TransferSuccessTypes.InternalBankTransfer)
    }
  }

  function onTransferError(
    backendError: IGQLErrorResponse | BackendError[] | GQLError,
    variables?: CreateBankInternalTransferInput,
  ): void {
    log.debug('onTransferError', { backendError, variables })
  }

  const { mutate: createInternalBankTransferMutation, isLoading: internalBankTransferLoading } =
    useCreateBankInternalTransfer({
      onSuccess: onTransferSuccess,
      onError: onTransferError,
    })

  return {
    createInternalBankTransferMutation,
    internalBankTransferLoading,
  }
}
